/**
 * Runtime configuration loader
 * Loads configuration from /config.json at runtime
 * Falls back to environment variables or defaults for local development
 */

export interface AppConfig {
  /** Base URL for the sovra-identity-app backend API */
  apiBaseUrl: string;
  /** OIDC configuration for authentication */
  oidc: {
    /** OIDC provider URL (e.g., https://authentication.nonprod.sovra.com) */
    authority: string;
    /** OAuth2 client ID when not using per-realm clients (see realmClients) */
    clientId: string;
    /**
     * When non-empty, each resolved realm_id must map to its Auth0 SPA client_id.
     * Enables SAML/HRD by avoiding a pinned `connection` on /authorize.
     */
    realmClients?: Record<string, string>;
    /** Redirect URI after authentication (optional — computed from window.location.origin at runtime) */
    redirectUri?: string;
    /** OAuth2 scopes (default: "openid profile email") */
    scope: string;
    /** API audience for access token */
    audience: string;
  };
}

function normalizeRealmClients(
  raw: unknown
): Record<string, string> | undefined {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return undefined;
  }
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof value === "string" && value.trim()) {
      out[key] = value.trim();
    }
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

/** True when config enables per-realm Auth0 SPA applications (realm_id → client_id). */
export function usesPerRealmOidcClients(config: AppConfig): boolean {
  const m = config.oidc.realmClients;
  return !!m && Object.keys(m).length > 0;
}

export type OidcClientIdResolution =
  | { ok: true; clientId: string }
  | {
      ok: false;
      reason: "missing_authority" | "missing_client" | "missing_realm_mapping";
    };

/**
 * Resolves the OAuth2 client_id for the current realm.
 * If `oidc.realmClients` has entries, the resolved realm_id must be present (no silent fallback).
 * Otherwise uses legacy `oidc.clientId`.
 */
export function resolveOidcClientId(
  config: AppConfig,
  realmId: string
): OidcClientIdResolution {
  const authority = config.oidc.authority?.trim();
  if (!authority) {
    return { ok: false, reason: "missing_authority" };
  }

  const rid = realmId.trim();
  if (usesPerRealmOidcClients(config)) {
    const mapped = rid ? config.oidc.realmClients?.[rid]?.trim() : "";
    if (!mapped) {
      return { ok: false, reason: "missing_realm_mapping" };
    }
    return { ok: true, clientId: mapped };
  }

  const legacy = config.oidc.clientId?.trim();
  if (!legacy) {
    return { ok: false, reason: "missing_client" };
  }
  return { ok: true, clientId: legacy };
}

let config: AppConfig | null = null;
let configLoadPromise: Promise<AppConfig> | null = null;

/**
 * Default configuration for local development
 */
function getDefaultConfig(): AppConfig {
  return {
    apiBaseUrl: import.meta.env.VITE_API_BASE_URL || "http://localhost:8030",
    oidc: {
      authority:
        import.meta.env.VITE_OIDC_AUTHORITY ||
        "https://authentication.nonprod.sovra.com",
      clientId: import.meta.env.VITE_OIDC_CLIENT_ID || "",
      scope: import.meta.env.VITE_OIDC_SCOPE || "openid profile email",
      audience:
        import.meta.env.VITE_OIDC_AUDIENCE || "https://identity.sovra.com",
    },
  };
}

/**
 * Load configuration from /config.json
 */
async function loadConfig(): Promise<AppConfig> {
  if (config) {
    return config;
  }

  if (configLoadPromise) {
    return configLoadPromise;
  }

  configLoadPromise = (async () => {
    try {
      const response = await fetch("/config.json");
      if (response.ok) {
        const runtimeConfig = await response.json();
        const realmClients = normalizeRealmClients(
          runtimeConfig.oidc?.realmClients
        );
        config = {
          apiBaseUrl:
            runtimeConfig.apiBaseUrl ||
            import.meta.env.VITE_API_BASE_URL ||
            "http://localhost:8030",
          oidc: {
            authority:
              runtimeConfig.oidc?.authority ||
              import.meta.env.VITE_OIDC_AUTHORITY ||
              "https://authentication.nonprod.sovra.com",
            clientId:
              runtimeConfig.oidc?.clientId ||
              import.meta.env.VITE_OIDC_CLIENT_ID ||
              "",
            ...(realmClients ? { realmClients } : {}),
            scope:
              runtimeConfig.oidc?.scope ||
              import.meta.env.VITE_OIDC_SCOPE ||
              "openid profile email",
            audience:
              runtimeConfig.oidc?.audience ||
              import.meta.env.VITE_OIDC_AUDIENCE ||
              "https://identity.sovra.com",
          },
        };
        console.log("[Config] Loaded from /config.json");
        return config;
      }
      console.warn("[Config] config.json not found, using defaults");
    } catch (error) {
      console.warn("[Config] Error loading config.json:", error);
    }

    // Fallback to default configuration
    config = getDefaultConfig();
    return config;
  })();

  return configLoadPromise;
}

/**
 * Get configuration synchronously
 * Returns current config or defaults if not loaded yet
 * For guaranteed loaded config, use getConfig()
 */
export function getConfigSync(): AppConfig {
  if (config) {
    return config;
  }
  return getDefaultConfig();
}

/**
 * Get configuration asynchronously
 * Loads from /config.json if available
 */
export async function getConfig(): Promise<AppConfig> {
  return loadConfig();
}

/**
 * Initialize configuration early
 * Call this in main.tsx before rendering
 */
export async function initConfig(): Promise<void> {
  await loadConfig();
}
